/**
 * Affordability actions on a quotation — the answer to lump-sum sticker shock.
 *
 * <AffordabilityBanner /> — "you never pay all at once" framing, shown once per page.
 * <QuoteNegotiationActions kind="bid"|"quote" id={..} total={..} currency={..} />
 *   — "Request milestone breakdown" and "Propose my budget" buttons; both send a
 *   structured chat message to the provider (in-app + email + WhatsApp).
 */
import React, { useState } from 'react';
import {
  Box, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, InputAdornment, Snackbar
} from '@mui/material';
import { ViewWeek, Savings } from '@mui/icons-material';
import api from '../services/api';

export function AffordabilityBanner({ sx }) {
  return (
    <Alert severity="info" icon={<Savings />} sx={{ ...sx }}>
      <strong>A quotation is a starting point, not all-or-nothing.</strong> You can pay it in full —
      or ask the contractor to break the work into milestones you fund one at a time, or tell them
      what you have available and they'll quote what it can cover.
    </Alert>
  );
}

export function QuoteNegotiationActions({ kind, id, total, currency = 'USD', size = 'small', sx }) {
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budget, setBudget] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // 'milestones' | 'budget'
  const [toast, setToast] = useState(null);

  const send = async (mode, amount) => {
    setSending(true);
    try {
      await api.post(`/quote-negotiation/${kind}/${id}`, { mode, amount, currency });
      setSent(mode);
      setBudgetOpen(false);
      setToast(mode === 'budget'
        ? 'Budget sent — the contractor will reply with what it can cover.'
        : 'Request sent — the contractor will reply with a milestone breakdown.');
    } catch (e) {
      setToast(e.response?.data?.message || 'Could not send — please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ...sx }}>
      <Button
        size={size} variant="outlined" startIcon={<ViewWeek />}
        disabled={sending || sent === 'milestones'}
        onClick={() => send('milestones')}
      >
        {sent === 'milestones' ? 'Breakdown requested ✓' : 'Request milestone breakdown'}
      </Button>
      <Button
        size={size} variant="outlined" color="success" startIcon={<Savings />}
        disabled={sending || sent === 'budget'}
        onClick={() => setBudgetOpen(true)}
      >
        {sent === 'budget' ? 'Budget proposed ✓' : 'Propose my budget'}
      </Button>

      <Dialog open={budgetOpen} onClose={() => setBudgetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Propose your budget</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tell the contractor what you have available to start. They'll reply with what that
            covers, or propose a first milestone that fits it. You're not committing to anything.
          </Typography>
          <TextField
            autoFocus fullWidth type="number" label="Amount you have available"
            value={budget} onChange={(e) => setBudget(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency === 'USD' ? '$' : currency}</InputAdornment> }}
            helperText={total ? `Quotation total: ${currency === 'USD' ? '$' : `${currency} `}${Number(total).toLocaleString()}` : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetOpen(false)}>Cancel</Button>
          <Button
            variant="contained" disabled={sending || !budget || Number(budget) <= 0}
            onClick={() => send('budget', Number(budget))}
          >
            Send to contractor
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}
